<template>
  <PmModalPure title="Ansicht erstellen" @close="emit('close')">
    <div class="PmViewCreatePure">
      <PmErrorNotificationPure
        v-if="error && errorMessage"
        class="PmViewCreatePure-notification"
        :message="errorMessage"
        :details="errorDetails"
      />

      <PmAppNotificationPure class="PmViewCreatePure-notification">
        Es wird eine neue Ansicht mit den aktuell angewendeten Einstellungen
        gespeichert.
      </PmAppNotificationPure>

      <form
        :id="`form${uid}`"
        class="PmViewCreatePure-form"
        @submit.prevent="create"
      >
        <PmInputPure
          v-model:value="name"
          label="Name"
          placeholder="Namen eingeben"
          class="PmViewCreatePure-input"
          required
          :disabled="loading"
          :not-allowed="existingViewNames"
          :trigger-validity-check="triggerValidityCheck"
          :validation-messages="{
            notAllowed:
              'Dieser Name existiert bereits für eine andere Ansicht. Bitte wähle einen eindeutigen Namen.',
          }"
        />

        <PmCheckboxPure
          v-if="userCanCreatePublicView"
          v-model:value="formIsPublicView"
          :disabled="loading"
          class="PmViewCreatePure-public"
          note="Öffentliche Ansichten können von anderen Benutzern verwendet werden. Admins können die Ansicht bearbeiten/löschen."
        >
          Öffentlich
        </PmCheckboxPure>
      </form>
    </div>

    <template #footer>
      <PmButtonListPure align="right">
        <PmButtonPure
          label="Abbrechen"
          :disabled="loading"
          @click="emit('close')"
        />

        <PmButtonPure
          label="Ansicht erstellen"
          variant="primary"
          type="submit"
          :form="`form${uid}`"
          :loading="loading"
        />
      </PmButtonListPure>
    </template>
  </PmModalPure>
</template>

<script lang="ts">
import { defineComponent } from 'vue'

const COMPONENT_NAME = 'PmViewCreatePure'

export const propTypes = {}

export default defineComponent({
  name: COMPONENT_NAME,
})
</script>

<script setup lang="ts">
import { ref } from 'vue'
import cuid from '@paralleldrive/cuid2'

import PmInputPure from '@/components/basics/PmInput/PmInputPure.vue'
import PmErrorNotificationPure from '@/components/basics/PmErrorNotificationPure.vue'
import PmCheckboxPure from '@/components/basics/PmCheckboxPure.vue'
import PmModalPure from '@/components/basics/PmModalPure.vue'
import PmButtonListPure from '@/components/basics/PmButtonListPure.vue'
import PmButtonPure from '@/components/basics/PmButtonPure.vue'
import PmAppNotificationPure from '@/components/basics/PmAppNotification/PmAppNotificationPure.vue'

export interface Props {
  loading?: boolean
  error?: boolean
  errorMessage?: string
  errorDetails?: string[]
  userCanCreatePublicView?: boolean
  existingViewNames?: string[]
}

const props = withDefaults(defineProps<Props>(), {
  existingViewNames: () => [],
})

const emit = defineEmits<{
  (event: 'create', payload: { name: string; isPublic: boolean }): void
  (event: 'close'): void
}>()

const uid = cuid.createId()

const name = ref<string>()
const formIsPublicView = ref(false)
const nameCustomValidity = ref<string>()
const triggerValidityCheck = ref(0)

function create() {
  triggerValidityCheck.value += 1

  if (!name.value) throw new Error('name is undefined')
  emit('create', { name: name.value, isPublic: formIsPublicView.value })
}
</script>

<style lang="scss">
.PmViewCreatePure {
  $block: &;

  &-notification {
    margin-bottom: var(--space-gutters);
  }

  &-form {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
}
</style>
